import { useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PrimaryBtnBlue } from './Buttons'
import Styles from './ImageModal.module.css'
import { Tip } from './Tip'
import { useUI } from '../context/UIContext'
import { useContentSharing } from '../context/ContentSharingContext'

export type ImageModalProps = {
    src: string
    alt?: string
    onClose?: () => void
}

export const ImageModal = () => {
    const { imageModal, closeImageModal } = useUI()
    const imageRef = useRef<HTMLImageElement>(null)

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeImageModal()
            }
        }

        window.addEventListener('keydown', handleEscKey)
        return () => window.removeEventListener('keydown', handleEscKey)
    }, [closeImageModal])

    if (!imageModal?.src) return null

    return (
        <div className={Styles.imageModal} onClick={closeImageModal}>
            <div className={Styles.imageContainer} onClick={(e) => e.stopPropagation()}>
                <img 
                    ref={imageRef}
                    src={imageModal.src}
                    alt={imageModal.alt || 'Image preview'}
                    className={Styles.modalImage}
                />

                <div className={Styles.controls}>
                    <PrimaryBtnBlue data-tooltip-id="image-modal-close" className="primary" onClick={closeImageModal}>
                        <FontAwesomeIcon icon={['fas', 'times']} />
                    </PrimaryBtnBlue>
                    <Tip id="image-modal-close" content="Close image" place={'left'} />
                </div>
            </div>
        </div>
    )
}

// Clickable image component that opens in the modal when clicked
export const ClickableImage = ({ src, alt, className, style }: { 
    src: string
    alt?: string
    className?: string
    style?: React.CSSProperties
}) => {
    const { openImageModal } = useUI()
    const { isContentSharer, shareImage } = useContentSharing()

    const handleClick = () => {
        openImageModal({ src, alt })
        
        // If this user is the content sharer, send command to all participants
        if (isContentSharer) {
            shareImage(src, alt)
        }
    }

    return (
        <img
            src={src}
            alt={alt || 'Clickable image'}
            className={`${Styles.clickableImage} ${className || ''}`}
            style={style}
            onClick={handleClick}
        />
    )
} 